export interface JackpotBandElements {
  container: HTMLElement;
  numberSpan: HTMLElement;
  currencySpan: HTMLElement;
}

type JackpotBandElementsGetter = () => JackpotBandElements | null;

const bandGetters = new Map<Element, JackpotBandElementsGetter>();
// Height is part of the key: media queries (e.g. pickRandom min-height) change band height and font size at equal width.
const lastFitSize = new WeakMap<Element, string>();
let sharedObserver: ResizeObserver | null = null;

const getContainerSize = (container: HTMLElement) => `${container.clientWidth}x${container.clientHeight}`;

const resetBand = ({ numberSpan }: JackpotBandElements) => {
  numberSpan.style.fontSize = '';
};

const measureBand = ({ container, numberSpan, currencySpan }: JackpotBandElements): number | null => {
  const initialFontSize = parseFloat(getComputedStyle(numberSpan).fontSize);

  const containerWidth = container.clientWidth;
  const currencyWidth = currencySpan.offsetWidth;
  const availableWidth = containerWidth - currencyWidth - 4;
  const numberWidth = numberSpan.scrollWidth;

  if (numberWidth > availableWidth && availableWidth > 0) {
    const scale = availableWidth / numberWidth;
    return Math.max(initialFontSize * scale, 8);
  }
  return null;
};

const applyBand = ({ numberSpan }: JackpotBandElements, fontSize: number | null) => {
  if (fontSize !== null) {
    numberSpan.style.fontSize = `${fontSize}px`;
  }
};

// Mounts, value changes and font loads are queued and fitted together once the effects of a commit have run:
// a row of cards mounting during fast scroll costs one forced layout for all its bands instead of two per band
// (the value effect and the mount effect both asked for a fit). Same reset → measure → apply as before.
const pendingFits = new Set<JackpotBandElementsGetter>();
let flushQueued = false;

const flushPendingFits = () => {
  flushQueued = false;
  const bands: JackpotBandElements[] = [];
  pendingFits.forEach((getElements) => {
    const band = getElements();
    if (band) bands.push(band);
  });
  pendingFits.clear();

  bands.forEach(resetBand);
  const fontSizes = bands.map(measureBand);
  bands.forEach((band, index) => applyBand(band, fontSizes[index]));
  bands.forEach((band) => lastFitSize.set(band.container, getContainerSize(band.container)));
};

export const scheduleJackpotBandFit = (getElements: JackpotBandElementsGetter) => {
  pendingFits.add(getElements);
  if (flushQueued) return;
  flushQueued = true;
  queueMicrotask(flushPendingFits);
};

// Batched so a rotation costs one forced layout for all bands instead of one per band.
const handleResize = (entries: ResizeObserverEntry[]) => {
  const bands: JackpotBandElements[] = [];
  const sizes: string[] = [];

  for (const entry of entries) {
    const band = bandGetters.get(entry.target)?.();
    if (!band) continue;
    const size = getContainerSize(band.container);
    if (lastFitSize.get(band.container) === size) continue;
    bands.push(band);
    sizes.push(size);
  }

  bands.forEach(resetBand);
  const fontSizes = bands.map(measureBand);
  bands.forEach((band, index) => {
    applyBand(band, fontSizes[index]);
    lastFitSize.set(band.container, sizes[index]);
  });
};

// A web font finishing loading changes every band's text width: one listener for all bands, not one per card.
const refitAllBands = () => {
  bandGetters.forEach((getElements) => scheduleJackpotBandFit(getElements));
};

export const observeJackpotBand = (container: HTMLElement, getElements: JackpotBandElementsGetter) => {
  if (!sharedObserver) {
    sharedObserver = new ResizeObserver(handleResize);
    document.fonts.addEventListener('loadingdone', refitAllBands);
  }
  bandGetters.set(container, getElements);
  sharedObserver.observe(container);
};

export const unobserveJackpotBand = (container: HTMLElement) => {
  bandGetters.delete(container);
  // Observed again later (same element), its first observer callback must fit it like a new band
  lastFitSize.delete(container);
  sharedObserver?.unobserve(container);
};
